import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const usePrevious = <T extends unknown>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

export const useKeyPress = (keys: string[], callback: (event: KeyboardEvent) => void, node = null) => {
  // implement the callback ref pattern
  const callbackRef = useRef(callback);
  useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  // handle what happens on key press
  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      // check if one of the key is part of the ones we want
      for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        if (key.startsWith('ctrl+')) {
          key = key.substring(5);
          if (event.key === key && event.ctrlKey === true) {
            callbackRef.current(event);
          }
        } else {
          if (event.key === key) {
            callbackRef.current(event);
          }
        }
      }
    },
    [keys],
  );

  useEffect(() => {
    // target is either the provided node or the document
    const targetNode = node ?? document;
    // attach the event listener
    if (targetNode) targetNode.addEventListener('keydown', handleKeyPress);

    // remove the event listener
    return () => targetNode && targetNode.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress, node]);
};

export const useWidth = <T extends HTMLElement>() => {
  const [width, setWidth] = useState<number | null>(null);
  const obsRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    if (node) {
      // One immediate synchronous read
      setWidth(node.getBoundingClientRect().width);
      // Live updates
      const ro = new ResizeObserver(([e]) => {
        const logical =
          (Array.isArray(e.contentBoxSize)
            ? e.contentBoxSize[0].inlineSize
            : // This is a workaround for older browsers which does not support contentBoxSize
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e as any).contentBoxSize?.inlineSize) ?? e.contentRect.width;
        setWidth(logical);
      });
      ro.observe(node);
      obsRef.current = ro; // save observer
    } else if (obsRef.current) {
      // cleanup when React passes null
      obsRef.current.disconnect();
      obsRef.current = null;
    }
  }, []);

  return { ref, width };
};

/**
 * The border-box height of an element, followed with a `ResizeObserver` while its content changes. A CSS
 * transform does not change it, so a scaled element reports its height before the scale.
 */
export const useHeight = <T extends HTMLElement>() => {
  const [height, setHeight] = useState<number | null>(null);
  const obsRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    obsRef.current?.disconnect();
    obsRef.current = null;
    if (!node) return;

    const measure = () => setHeight(node.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node, { box: 'border-box' });
    obsRef.current = ro;
  }, []);

  return { ref, height };
};
